/**
 * Pure model-building for the Trends tab: monthly non-cumulative spending
 * lines, this year against last, with group-level isolate/compare.
 *
 * Spend classification follows the flow model's offset rule at the window
 * level: a category is a spend category when its net over the fetched
 * two-year window is negative; inflows inside it reduce its monthly value
 * rather than counting as income (a refund-heavy month can dip below
 * zero — that is honest, not an error). Income-side categories never
 * contribute. Amounts are integer cents; plotted values are positive for
 * spending (−net).
 */

export type TrendsRow = {
  /** Category id; null is the server's bucket for uncategorized rows. */
  key: string | null;
  /** UTC calendar month, "2026-03". */
  month: string;
  net: number;
};

export type TrendsCategoryGroupInfo = {
  _id: string;
  name: string;
  categoryIds: string[];
  color: string | null;
};

/** The pseudo-group holding spend categories that belong to no group. */
export const UNGROUPED_ID = '__ungrouped';

export type TrendsChip = {
  id: string;
  label: string;
  /** User-chosen color name from the category color list, when set. */
  color: string | null;
  /**
   * Stable palette slot: the chip's position in its sorted row. Lines
   * derive their fallback color from this, never from selection order, so
   * a series always matches its chip's dot.
   */
  paletteIndex: number;
  /** Base-year spend total, for chip ordering and quick reading. */
  baseTotal: number;
};

export type TrendsSeries = {
  id: string;
  /** "Home · 2026" — tooltip and accessibility name. */
  label: string;
  /** Color slot the renderer resolves (group color name or null). */
  color: string | null;
  /** Palette index used when `color` is null. */
  paletteIndex: number;
  dashed: boolean;
  /**
   * Marks the series' last drawn point: 'dot' draws the frontier dot,
   * 'dot-label' additionally writes the year beside it. Solid base-year
   * lines carry the frontier marker; end labels appear only when few
   * enough lines share the chart to keep them legible.
   */
  endMarker: 'none' | 'dot' | 'dot-label';
  /** Text for the end label ("2026"). */
  endLabel: string;
  /**
   * One value per month, January first. Null months draw no point — the
   * base year's future months when it is the current year.
   */
  points: (number | null)[];
};

/** One band of the stacked view: the base year only, clamped at zero. */
export type TrendsBand = {
  id: string;
  label: string;
  color: string | null;
  paletteIndex: number;
  points: (number | null)[];
};

/** One row of a month's "what moved" panel. */
export type TrendsMonthRow = {
  key: string | null;
  label: string;
  /** Spend-positive cents; null when the base month is past the frontier. */
  base: number | null;
  prev: number;
  /** base − prev; null when base is. */
  delta: number | null;
};

export type TrendsTotals = {
  /** Months compared (1-based count from January). */
  months: number;
  base: number;
  prev: number;
};

export type TrendsModel = {
  chips: TrendsChip[];
  /** Member-category chips when exactly one group is isolated. */
  categoryChips: TrendsChip[];
  /**
   * The selections that actually apply, after dropping ids whose chip no
   * longer exists (a year or filter change can remove one). The view
   * renders chip active-states from these, not from its raw state, so the
   * display never claims a hidden selection.
   */
  activeGroupIds: string[];
  activeCategoryIds: string[];
  /** The taxes chip's id when the binding resolves to a spend category. */
  taxesChipId: string | null;
  /**
   * Per month (January first): category rows within the current scope,
   * this year vs the same month last year, sorted by |delta| — the answer
   * to "why did this month jump".
   */
  monthDetails: TrendsMonthRow[][];
  series: TrendsSeries[];
  /**
   * The stacked view over the same scope: base-year bands (negative
   * refund months clamped to zero so the stack holds) under the prior
   * year's total as a dashed overlay. With nothing selected the bands
   * are every group — composition is the stacked view's whole point.
   */
  stackBands: TrendsBand[];
  stackPrevTotal: TrendsSeries;
  totals: TrendsTotals;
};

type BuildTrendsModelArgs = {
  rows: TrendsRow[];
  categoriesById: Map<string, {name: string}>;
  categoryGroups: TrendsCategoryGroupInfo[];
  /** Category-filter scoping; null means everything counts. */
  filterCategoryIds: string[] | null;
  baseYear: number;
  /** 0-based month through which the base year has data (11 = full year). */
  baseYearLastMonth: number;
  selectedGroupIds: string[];
  selectedCategoryIds: string[];
  /**
   * The bound taxes category: excluded from "All spending" and from its
   * group — tax payments are not consumption, and refunds carve dips that
   * read as spending swings. It gets its own trailing chip instead, so
   * its line is one click away without polluting the default.
   */
  taxesCategoryId: string | null;
};

/**
 * How many line pairs may carry end labels before they collide — beyond
 * this the chips remain the legend and lines end with bare dots.
 */
const MAX_LABELED_SCOPES = 1;

const MONTHS = 12;

const monthIndex = (month: string, year: number): number | null => {
  const [y, m] = month.split('-').map(Number);
  return y === year && m >= 1 && m <= MONTHS ? m - 1 : null;
};

/** Sum of −net per month for a set of category keys (null = uncategorized). */
const seriesPoints = (
  perCategory: Map<string | null, Map<string, number>>,
  keys: Set<string | null>,
  year: number,
  lastMonth: number,
): (number | null)[] => {
  const points: (number | null)[] = Array.from({length: MONTHS}, (_v, i) =>
    i <= lastMonth ? 0 : null,
  );
  for (const key of keys) {
    const byMonth = perCategory.get(key);
    if (byMonth == null) {
      continue;
    }
    for (const [month, net] of byMonth) {
      const index = monthIndex(month, year);
      if (index != null && index <= lastMonth) {
        points[index] = (points[index] ?? 0) - net;
      }
    }
  }
  return points;
};

const sumPoints = (points: (number | null)[], through: number): number =>
  points.reduce<number>(
    (sum, value, i) => (i <= through && value != null ? sum + value : sum),
    0,
  );

export const buildTrendsModel = ({
  rows,
  categoriesById,
  categoryGroups,
  filterCategoryIds,
  baseYear,
  baseYearLastMonth,
  selectedGroupIds,
  selectedCategoryIds,
  taxesCategoryId,
}: BuildTrendsModelArgs): TrendsModel => {
  // The echo year: with the base year unfinished, the year BEFORE the
  // comparison year fills the unlived months (dashed, from the frontier),
  // so the chart's right half reads "what the last two years did next"
  // instead of trailing off. Only meaningful when there is a gap.
  const echoYear = baseYear - 2;
  const hasEcho = baseYearLastMonth < MONTHS - 1;
  const filter =
    filterCategoryIds != null ? new Set<string | null>(filterCategoryIds) : null;

  // category → month → net, filter applied. The uncategorized bucket is a
  // real spend population and participates under the null key.
  // Classification (`windowNet`) sees only the months the chart shows —
  // the base year past its last complete month is excluded, so an
  // in-progress month can neither mint a $0 chip nor flip a category off
  // the spend side with a large refund.
  const perCategory = new Map<string | null, Map<string, number>>();
  const windowNet = new Map<string | null, number>();
  // The echo year classifies separately: a category retired since then
  // still belongs on the echo line, or its history understates.
  const echoNet = new Map<string | null, number>();
  for (const row of rows) {
    if (filter != null && (row.key == null || !filter.has(row.key))) {
      continue;
    }
    const byMonth = perCategory.get(row.key) ?? new Map<string, number>();
    byMonth.set(row.month, (byMonth.get(row.month) ?? 0) + row.net);
    perCategory.set(row.key, byMonth);
    const baseIndex = monthIndex(row.month, baseYear);
    const inEchoYear = monthIndex(row.month, echoYear) != null;
    if (inEchoYear) {
      echoNet.set(row.key, (echoNet.get(row.key) ?? 0) + row.net);
    } else if (baseIndex == null || baseIndex <= baseYearLastMonth) {
      windowNet.set(row.key, (windowNet.get(row.key) ?? 0) + row.net);
    }
  }

  const spendKeys = new Set<string | null>(
    [...windowNet.entries()]
      .filter(([, net]) => net < 0)
      .map(([key]) => key),
  );
  const echoSpendKeys = new Set<string | null>(
    [...echoNet.entries()].filter(([, net]) => net < 0).map(([key]) => key),
  );

  // Taxes leaves every pool before grouping — it joins neither the total
  // nor its group's members nor the echo, regardless of which windows it
  // spent in (echo-only tax history must not leak spikes back into the
  // default line). The chip alone is gated on the comparison window
  // having something to show.
  const taxesKey =
    taxesCategoryId != null && spendKeys.has(taxesCategoryId)
      ? taxesCategoryId
      : null;
  if (taxesCategoryId != null) {
    spendKeys.delete(taxesCategoryId);
    echoSpendKeys.delete(taxesCategoryId);
  }

  // Partition the spend keys into groups; ungrouped ones (and the
  // uncategorized bucket) pool under the pseudo-group.
  const groupOf = new Map<string, TrendsCategoryGroupInfo>();
  for (const group of categoryGroups) {
    for (const id of group.categoryIds) {
      groupOf.set(id, group);
    }
  }
  const membersByGroup = new Map<string, Set<string | null>>();
  for (const key of spendKeys) {
    const group = key != null ? groupOf.get(key) : undefined;
    const groupId = group?._id ?? UNGROUPED_ID;
    const members = membersByGroup.get(groupId) ?? new Set<string | null>();
    members.add(key);
    membersByGroup.set(groupId, members);
  }

  const baseTotalOf = (keys: Set<string | null>): number =>
    sumPoints(
      seriesPoints(perCategory, keys, baseYear, baseYearLastMonth),
      baseYearLastMonth,
    );

  const labelFor = (key: string | null): string =>
    key == null
      ? 'Uncategorized'
      : (categoriesById.get(key)?.name ?? 'Unknown category');

  const chips: TrendsChip[] = [...membersByGroup.entries()]
    .map(([groupId, members]) => {
      const group = categoryGroups.find((g) => g._id === groupId);
      return {
        id: groupId,
        label: group?.name ?? 'Ungrouped',
        color: group?.color ?? null,
        baseTotal: baseTotalOf(members),
      };
    })
    .sort((a, b) => b.baseTotal - a.baseTotal || a.label.localeCompare(b.label))
    // The taxes chip trails regardless of size — it is excluded context,
    // not a competitor for attention.
    .concat(
      taxesKey != null
        ? [
            {
              id: taxesKey,
              label: labelFor(taxesKey),
              color: null,
              baseTotal: baseTotalOf(new Set([taxesKey])),
            },
          ]
        : [],
    )
    .map((chip, index): TrendsChip => ({...chip, paletteIndex: index}));

  const activeGroups = selectedGroupIds.filter(
    (id) => membersByGroup.has(id) || id === taxesKey,
  );

  const categoryChips: TrendsChip[] =
    activeGroups.length === 1 && membersByGroup.has(activeGroups[0])
      ? [...(membersByGroup.get(activeGroups[0]) ?? [])]
          .map((key) => {
            const keys = new Set<string | null>([key]);
            return {
              id: key ?? UNGROUPED_ID,
              label:
                key == null
                  ? 'Uncategorized'
                  : (categoriesById.get(key)?.name ?? 'Unknown category'),
              color: null,
              baseTotal: baseTotalOf(keys),
            };
          })
          .sort(
            (a, b) =>
              b.baseTotal - a.baseTotal || a.label.localeCompare(b.label),
          )
          .map((chip, index): TrendsChip => ({...chip, paletteIndex: index}))
      : [];

  // What gets drawn: the total pair, one pair per selected group, or one
  // pair per selected member category of a single isolated group.
  type SeriesScope = {
    id: string;
    label: string;
    color: string | null;
    paletteIndex: number;
    keys: Set<string | null>;
    /**
     * Keys for the echo series: `keys` plus categories that spent only in
     * the echo year. Harmless as a superset — echo points read only
     * echo-year months.
     */
    echoKeys?: Set<string | null>;
  };
  let scopes: SeriesScope[];
  const activeCategories =
    activeGroups.length === 1
      ? selectedCategoryIds.filter((id) =>
          categoryChips.some((chip) => chip.id === id),
        )
      : [];
  const chipSlot = (row: TrendsChip[], id: string): number =>
    row.find((chip) => chip.id === id)?.paletteIndex ?? 0;
  const echoKeysForGroup = (groupId: string): Set<string | null> => {
    const keys = new Set(membersByGroup.get(groupId) ?? []);
    for (const key of echoSpendKeys) {
      const group = key != null ? groupOf.get(key) : undefined;
      if ((group?._id ?? UNGROUPED_ID) === groupId) {
        keys.add(key);
      }
    }
    return keys;
  };
  if (activeGroups.length === 0) {
    scopes = [
      {
        id: 'total',
        label: 'All spending',
        color: null,
        paletteIndex: -1,
        keys: spendKeys,
        echoKeys: new Set([...spendKeys, ...echoSpendKeys]),
      },
    ];
  } else if (activeCategories.length > 0) {
    scopes = activeCategories.map((id) => ({
      id,
      label:
        id === UNGROUPED_ID
          ? 'Uncategorized'
          : (categoriesById.get(id)?.name ?? 'Unknown category'),
      color: null,
      paletteIndex: chipSlot(categoryChips, id),
      keys: new Set<string | null>([id === UNGROUPED_ID ? null : id]),
    }));
  } else {
    scopes = activeGroups.map((groupId) => {
      if (groupId === taxesKey) {
        const keys = new Set<string | null>([taxesKey]);
        return {
          id: groupId,
          label: labelFor(taxesKey),
          color: null,
          paletteIndex: chipSlot(chips, groupId),
          keys,
          echoKeys: keys,
        };
      }
      const group = categoryGroups.find((g) => g._id === groupId);
      return {
        id: groupId,
        label: group?.name ?? 'Ungrouped',
        color: group?.color ?? null,
        paletteIndex: chipSlot(chips, groupId),
        keys: membersByGroup.get(groupId) ?? new Set<string | null>(),
        echoKeys: echoKeysForGroup(groupId),
      };
    });
  }

  const labeled = scopes.length <= MAX_LABELED_SCOPES;
  const series: TrendsSeries[] = scopes.flatMap((scope): TrendsSeries[] => [
    {
      id: `${scope.id}:base`,
      label: `${scope.label} · ${baseYear}`,
      color: scope.color,
      paletteIndex: scope.paletteIndex,
      dashed: false,
      // The frontier dot marks where the year currently ends; the year
      // label joins it when the chart is sparse enough to stay legible.
      endMarker: labeled ? 'dot-label' : 'dot',
      endLabel: String(baseYear),
      points: seriesPoints(perCategory, scope.keys, baseYear, baseYearLastMonth),
    },
    {
      id: `${scope.id}:prev`,
      label: `${scope.label} · ${baseYear - 1}`,
      color: scope.color,
      paletteIndex: scope.paletteIndex,
      dashed: true,
      endMarker: labeled ? 'dot-label' : 'none',
      endLabel: String(baseYear - 1),
      points: seriesPoints(perCategory, scope.keys, baseYear - 1, MONTHS - 1),
    },
    // The echo: the prior-prior year, dashed and muted, drawn only for
    // the months after the frontier — context for the remainder, never
    // competing with the lived months.
    ...(hasEcho && labeled
      ? [
          {
            id: `${scope.id}:echo`,
            label: `${scope.label} · ${echoYear}`,
            color: scope.color,
            paletteIndex: scope.paletteIndex,
            dashed: true,
            endMarker: 'dot-label' as const,
            endLabel: String(echoYear),
            points: seriesPoints(
              perCategory,
              scope.echoKeys ?? scope.keys,
              echoYear,
              MONTHS - 1,
            ).map((value, index) =>
              index > baseYearLastMonth ? value : null,
            ),
          },
        ]
      : []),
  ]);

  // Stacked view: bands over the current scope — or every group when
  // nothing is selected, since composition is the stacked view's point.
  const bandScopes =
    activeGroups.length > 0
      ? scopes
      : chips
          .filter((chip) => chip.id !== taxesKey)
          .map((chip) => ({
            id: chip.id,
            label: chip.label,
            color: chip.color,
            paletteIndex: chip.paletteIndex,
            keys: membersByGroup.get(chip.id) ?? new Set<string | null>(),
          }));
  const stackBands: TrendsBand[] = bandScopes.map((scope) => ({
    id: scope.id,
    label: scope.label,
    color: scope.color,
    paletteIndex: scope.paletteIndex,
    points: seriesPoints(
      perCategory,
      scope.keys,
      baseYear,
      baseYearLastMonth,
    ).map((value) => (value == null ? null : Math.max(0, value))),
  }));
  // The overlay must compare the same metric the bands draw: each scope
  // clamps at zero per month before summing, or a refund in one group
  // would depress a total whose base-year counterpart it cannot touch.
  const stackPrevPoints: (number | null)[] = Array.from(
    {length: MONTHS},
    () => 0,
  );
  for (const scope of bandScopes) {
    const points = seriesPoints(perCategory, scope.keys, baseYear - 1, MONTHS - 1);
    points.forEach((value, index) => {
      stackPrevPoints[index] =
        (stackPrevPoints[index] ?? 0) + Math.max(0, value ?? 0);
    });
  }
  const stackPrevTotal: TrendsSeries = {
    id: 'stack:prev',
    label: `Total · ${baseYear - 1}`,
    color: null,
    paletteIndex: -1,
    dashed: true,
    endMarker: 'dot-label',
    endLabel: String(baseYear - 1),
    points: stackPrevPoints,
  };

  // The caption compares like months: through the base year's last month.
  const totals: TrendsTotals = {
    months: baseYearLastMonth + 1,
    base: scopes.reduce(
      (sum, scope) =>
        sum +
        sumPoints(
          seriesPoints(perCategory, scope.keys, baseYear, baseYearLastMonth),
          baseYearLastMonth,
        ),
      0,
    ),
    prev: scopes.reduce(
      (sum, scope) =>
        sum +
        sumPoints(
          seriesPoints(perCategory, scope.keys, baseYear - 1, MONTHS - 1),
          baseYearLastMonth,
        ),
      0,
    ),
  };

  // "What moved": per month, each scoped category this year against the
  // same month last year, sorted by the size of the change.
  const detailKeys = [
    ...new Set(scopes.flatMap((scope) => [...scope.keys])),
  ];
  const monthDetails: TrendsMonthRow[][] = Array.from(
    {length: MONTHS},
    (_v, monthIdx) =>
      detailKeys
        .map((key): TrendsMonthRow => {
          const keys = new Set<string | null>([key]);
          const basePoints = seriesPoints(
            perCategory,
            keys,
            baseYear,
            baseYearLastMonth,
          );
          const prevPoints = seriesPoints(
            perCategory,
            keys,
            baseYear - 1,
            MONTHS - 1,
          );
          const base = basePoints[monthIdx];
          const prev = prevPoints[monthIdx] ?? 0;
          return {
            key,
            label: labelFor(key),
            base,
            prev,
            delta: base == null ? null : base - prev,
          };
        })
        .filter((row) => (row.base ?? 0) !== 0 || row.prev !== 0)
        .sort(
          (a, b) =>
            Math.abs(b.delta ?? b.prev) - Math.abs(a.delta ?? a.prev) ||
            a.label.localeCompare(b.label),
        ),
  );

  return {
    chips,
    categoryChips,
    activeGroupIds: activeGroups,
    activeCategoryIds: activeCategories,
    taxesChipId: taxesKey,
    monthDetails,
    series,
    stackBands,
    stackPrevTotal,
    totals,
  };
};
